import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Credentials,
  ExternalCredentials,
  NewCredentials,
} from '../models/credentials';
import { environment } from '../../../environments/environment';
import { Observable, of } from 'rxjs';
import { AuthenticationResult } from '../../shared/models/authentication-result';
import { tap, catchError, map } from 'rxjs/operators';
import { Constants } from '../models/constants';
import { UiStateStore } from '../../shared/store/ui-state-store.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { Account } from '../../signup/models/account';
import { AccountTypes } from '../../shared/models/AccountTypes';
import * as auth from 'firebase/auth';
import {
  getAuth,
  signInWithPopup,
  OAuthProvider,
  User,
  UserCredential,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  authenticationResult: AuthenticationResult;

  constructor(
    private httpClient: HttpClient,
    private uiStore: UiStateStore,
    public afAuth: AngularFireAuth,
    public router: Router,
    public ngZone: NgZone
  ) { }

  fireActivateAccount(oobCode:string):Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.afAuth.applyActionCode(oobCode).then(response => {
        resolve("success");
      }).catch(err => {
        reject(err);
      });
    });
  }

  fireSignIn(
    credentials: Credentials
  ): Promise<firebase.default.auth.UserCredential | string> {
    return new Promise<firebase.default.auth.UserCredential | string>(
      (resolve, reject) => {
        this.afAuth
          .signInWithEmailAndPassword(
            credentials.username,
            credentials.password
          )
          .then((res) => {
            if(!res?.user?.emailVerified) {
              return reject("Your email address is not yet verified. Please verify your email by clicking on the link sent to you in an email.");
            }
            const token = res?.user
              .getIdToken()
              .then((t) => {
                const externalCredentials = {
                  externalAccountId: res?.user?.uid,
                  externalToken: t,
                  accountType: credentials.accountType,
                } as ExternalCredentials;
                this.exteranlSignIn(externalCredentials).subscribe(
                  (s) => resolve(res),
                  (err) => reject(err)
                );
              })
              .catch((err) => {
                return reject(err);
              });
          })
          .catch((error) => {
            return reject(error);
          });
      }
    );
  }

  public async updatePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    return new Promise<any>((resolve, reject) => {
      const auth = getAuth();
      const user = <User>auth.currentUser;
      const credentials = EmailAuthProvider.credential(
        <string>user.email,
        currentPassword
      );
      try {
        reauthenticateWithCredential(user, credentials)
          .then((res) => {
            updatePassword(user, newPassword).then(
              (r) => resolve(res),
              (e) => reject(e)
            );
          })
          .catch((error) => {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  fireSignUp(email: string, password: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.afAuth
        .createUserWithEmailAndPassword(email, password)
        .then((result) => {
          const account = {
            email: email,
            externalAccountId: result.user?.uid,
          } as Account;
          this.createAccount(account).subscribe((account) => {
            result.user?.sendEmailVerification();
            resolve("");
          });
        })
        .catch((error) => {
          //this.uiStore.setError(`Error when signing up. ${error.message}`);
          reject(error.message);
        });
    });
  }

  fireSignOut(): Promise<void> {
    return this.afAuth
      .signOut()
      .then(() => {
        this.signOut();
      })
      .catch((err) =>
        this.uiStore.setError(`Error when signing out. ${err['error']}`)
      );
  }

  gmailSignIn(
    accountType: AccountTypes
  ): Promise<firebase.default.auth.UserCredential> {
    const googleProvider = new auth.GoogleAuthProvider();
    return this.providerSignIn(googleProvider, accountType);
  }

  private providerSignIn(
    provider: any,
    accountType: AccountTypes
  ): Promise<firebase.default.auth.UserCredential> {
    return new Promise<firebase.default.auth.UserCredential>(
      (resolve, reject) => {
        this.afAuth
          .signInWithPopup(provider)
          .then((res) => {
            const account = {
              email: res.additionalUserInfo.profile['email'],
              externalAccountId: res.user.uid,
            } as Account;
            this.createAccount(account).subscribe((account) => {
              const token = res.user
                .getIdToken()
                .then((t) => {
                  const externalCredentials = {
                    externalAccountId: res.user.uid,
                    externalToken: t,
                    accountType: accountType,
                  } as ExternalCredentials;
                  this.exteranlSignIn(externalCredentials).subscribe(
                    (s) => resolve(res),
                    (err) => reject(err)
                  );
                })
                .catch((err) => {
                  return reject(err);
                });
            });
          })
          .catch((error) => {
            window.alert(error);
          });
      }
    );
  }

  private createAccount(account: Account): Observable<Account> {
    let url = `${environment.baseApiUrl}/accounts`;
    return this.httpClient.post<Account>(url, account);
  }

  private exteranlSignIn(
    credentials: ExternalCredentials
  ): Observable<AuthenticationResult> {
    let url = `${environment.baseApiUrl}/accounts/externalsignIn`;
    return this.httpClient.post<AuthenticationResult>(url, credentials).pipe(
      tap((res) => {
        this.uiStore.setAuthenticationResult(res);
        this.setAthenticationToken(res.token);
      })
    );
  }

  signIn(
    credentials: Credentials,
    newSignup: boolean = false
  ): Observable<AuthenticationResult> {
    let url = `${environment.baseApiUrl}/accounts/signin`;
    return this.httpClient.post<AuthenticationResult>(url, credentials).pipe(
      tap((res) => {
        res.newSignup = newSignup;
        this.uiStore.setAuthenticationResult(res);
        this.setAthenticationToken(res.token);
      })
    );
  }

  signOut(): void {
    let url = `${environment.baseApiUrl}/accounts/signout`;
    let authenticationToken = this.getAthenticationToken();

    this.httpClient
      .post<AuthenticationResult>(url, authenticationToken)
      .subscribe(
        (result) => {
          this.clearAuthenticationToken();
        },
        (error) => {
          this.uiStore.setError(`Error when signing out. ${error['error']}`);
        }
      );
  }

  clearAuthenticationToken(): void {
    this.uiStore.setAuthenticationResult(null);
    localStorage.removeItem(Constants.AuthtokenKey);
    localStorage.removeItem(Constants.AuthenticationResult);
  }

  getAthenticationToken(): string {
    return localStorage.getItem(Constants.AuthtokenKey);
  }

  setAthenticationToken(token: string): void {
    localStorage.setItem(Constants.AuthtokenKey, token);
  }

  changePassword(newCredentials: NewCredentials): Observable<boolean> {
    let url = `${environment.baseApiUrl}/accounts/changepassword`;
    return this.httpClient.post<AuthenticationResult>(url, newCredentials).pipe(
      map(
        (res) => {
          return true;
        },
        catchError((error) => {
          this.uiStore.setError(
            `Error when changing password. ${error['error']}`
          );
          return of(false);
        })
      )
    );
  }

  activateAccount(accountId: string): Observable<boolean> {
    let url = `${environment.baseApiUrl}/accounts/activate/${accountId}`;
    return this.httpClient.get<boolean>(url).pipe(
      map(
        (res) => {
          return true;
        },
        catchError((error) => {
          this.uiStore.setError(
            `Error when activating account. ${error.message}`
          );
          return of(false);
        })
      )
    );
  }

  getAuthenticatedResult(): AuthenticationResult {
    return JSON.parse(
      localStorage.getItem(Constants.AuthenticationResult)
    ) as AuthenticationResult;
  }

  private setAuthenticationResult(
    account: Account,
    user: firebase.default.User
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const authResult = new AuthenticationResult();
      authResult.accountId = account.id;
      authResult.accountType = account.type;
      user.getIdToken().then((token) => {
        if (!token) {
          return reject('Token not retrieved');
        }

        authResult.token = token;
        authResult.userName = user.displayName;
        this.uiStore.setAuthenticationResult(authResult);
        this.setAthenticationToken(authResult.token);
        return resolve();
      });
    });
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    await this.afAuth
      .sendPasswordResetEmail(email)
      .then(() => {
        this.router.navigate(['/reset-confirm']);
      })
      .catch((error) => {
        window.alert(error);
      });
  }
  async verifyPasswordResetCode(code: string) {
    return await this.afAuth
      .verifyPasswordResetCode(code)
      .then((email) => {
        return email;
      })
      .catch((error) => {
        console.log(error.message);
      });
  }
  async confirmPasswordReset(code: string, newPassword): Promise<boolean> {
    return await this.afAuth
      .confirmPasswordReset(code, newPassword)
      .then(() => {
        return true;
      })
      .catch((error) => {
        console.log(error.message);
        return false;
      });
  }
}
